import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, combineLatest, switchMap } from 'rxjs';
import { AmountFormatPipe } from 'src/app/shared/Utils/amount-format-pipe.pipe';
import { TableEvent } from 'src/app/shared/components/table/Itable';
import { IPagination } from 'src/app/shared/models/pagination';
import { SharedModule } from 'src/app/shared/shared.module';
import { TableOption } from '../../interfaces/Iip-table-options';
import FinSupplierAccountRow from '../../interfaces/fin-supplier-account-row';
import IpErrorResponse from '../../interfaces/ip-error-response';
import FilterValues from '../../interfaces/ip-filter-values';
import InvoiceRow from '../../interfaces/ip-invoice-row';
import Pageable from '../../interfaces/ip-pageable';
import SettlementRow from '../../interfaces/ip-settlement-row';
import SupplierResponse from '../../interfaces/ip-supplier-response';
import SupplierRow from '../../interfaces/ip-supplier-row';
import { FnFiltersModalComponent } from '../../module/finance/components/fn-filters-modal/fn-filters-modal.component';
import { FnSearchInputComponent } from '../../module/finance/components/fn-search-input/fn-search-input.component';
import { FnAccountFinanceService } from '../../services/fn-account-finance.service';
import { IpCurrencyService } from '../../services/ip-currency.service';
import { IpRoleService } from '../../services/ip-role.service';
import { IpSettlementService } from '../../services/ip-settlement.service';
import { IpSnackbarService } from '../../services/ip-snackbar.service';
import { IpSupplierService } from '../../services/ip-supplier.service';
import { IpTableBillService } from '../../services/ip-table-bill.service';
import { TableService } from '../../types/ip-table-service';
import { IpFiltersComponent } from '../ip-filters/ip-filters.component';
import { IpSearchInputComponent } from '../ip-search-input/ip-search-input.component';
import { CustomDatePipe } from 'src/app/shared/Utils/pipeCustomDate';
import { ActivatedRoute } from '@angular/router';
import { MatDrawerMode } from '@angular/material/sidenav';
import { ResponseData } from '../../interfaces/ip-response-data';
import { FilterCacheService } from 'src/app/shared/services/filter-cache.service';

@Component({
  standalone: true,
  imports: [
    SharedModule,
    IpSearchInputComponent,
    IpFiltersComponent,
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FnSearchInputComponent,
  ],
  selector: 'app-ip-table',
  templateUrl: './ip-table.component.html',
  styleUrls: ['./ip-table.component.scss'],
  providers: [CustomDatePipe],
})
export class IpTableComponent implements OnInit, OnDestroy, OnChanges {
  @Input() noData: string = '';
  @Input() Date: string = '';
  @Input() supplierId!: number | undefined;
  @Input() propertyOrder: string[] = [];
  @Input() tableTitle!: string;
  @Input() pageItems!: number;
  @Input() tableOption!: TableOption;
  @Input() service!: TableService;
  @Input() dataChange!: Observable<void>;
  @Input() onlyLast30days: boolean = false;
  @Input() ignoredTypeStatus?: number;
  @Input() billed?: boolean | undefined;
  @Input() optionalTitle?: string;
  @Input() dataOptional?: any;
  @Input() chargeNewDate?: any;
  @Input() paidOrders?: boolean;
  @Input() scroll = false;
  @Input() searchField = true;
  @Input() payInConciliated = false;
  @Input() noTopOptions = false;
  @Input() filterKey?: string; 

  maxDate!: string;
  minEndDate!: string;
  datesForm!: FormGroup;
  showBtnFilters: boolean = true;

  get creationDate() {
    return this.datesForm.get('creationDate');
  }

  get dueDate() {
    return this.datesForm.get('dueDate');
  }

  type!: 'business' | 'employee' | 'provider';
  data?: any[];
  sortOrder: string = '';
  sortFieldName: string = '';
  pagination!: IPagination;
  totalItems!: number;
  dataChangeSubscription!: Subscription;
  typeStatus: string | Function = '';
  statusCode: string | Function = '';
  currentPage: number = 0;
  test: TableService = 'invoice';
  subscription?: Subscription;
  mode?: MatDrawerMode;
  selectFormControl: FormControl<any> = new FormControl<any>(0);
  mobileCardConfig: any;

  constructor(
    private roleService: IpRoleService,
    private tableBillService: IpTableBillService,
    private ipSupplierService: IpSupplierService,
    private ipSnackbarService: IpSnackbarService,
    private ipCurrencyService: IpCurrencyService,
    private amountFormatPipe: AmountFormatPipe,
    private ipSettlementService: IpSettlementService,
    private fnAccountFinanceService: FnAccountFinanceService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private readonly filterCacheService: FilterCacheService
  ) {this.updateMode(window.innerWidth)}

  get creationDateControl(){
    return this.datesForm.get('creationDate') as FormControl;
  }
  get dueDateControl(){
    return this.datesForm.get('dueDate') as FormControl;
  }

  ngOnInit(): void {
    this.type = this.roleService.getType();

    this.pagination = {
      page: 0,
      size: this.pageItems,
    };
    if (this.dataChange) {
      this.dataChangeSubscription = this.dataChange.subscribe({
        error: console.log,
        next: () => this.executeService(),
      });
    }

    if (this.onlyLast30days) {
      const date = new Date();
      this.filterValues.createDateTo = date
        .toISOString()
        .slice(0, 10)
        .concat('T23:59:59');
      this.filterValues.createDateFrom = new Date(
        date.setDate(date.getDate() - 30)
      )
        .toISOString()
        .slice(0, 10)
        .concat('T00:00:00');
    }
    // FORM
    this.datesForm = this.fb.group({
      creationDate: [''],
      dueDate: [''],
    });
    if(this.service === 'invoice-paid') {
      this.typeStatus = this.tableOption.selects![0][1];
      this.showBtnFilters = false;
    }
    else if (this.service === 'surrenders-paid') {
      this.typeStatus = this.tableOption.selects![0][1];
    } else if (this.service === 'settlements') {
      this.statusCode = this.tableOption.selects![0][1];
    }
    if(!this.filterKey || !this.filterCacheService.exists(this.filterKey)){
      this.executeService();
    }
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['moreFilterValues'] && !changes['moreFilterValues'].firstChange) {
      this.pagination = {
        page: 0,
        size: this.pageItems,
      };
      this.executeService()
    }
    if (changes['responseData'] && !changes['responseData'].firstChange) {
      if (this.responseData){
        switch (this.responseData.type) {
          case 'edit':
            if (this.data){
              this.data = this.data.map(d=> d.id === this.responseData?.data.id ? this.responseData?.data : d)
            }
            break;
          case 'delete':
            if (this.data){
              this.data = this.data.filter(d=>d.id !== this.responseData?.data.id)
            }
            break;
          case 'add':
            if (this.data){
              this.data.push(this.responseData.data)
            }
            break;
          default:
            break;
        }
      }
    }
  }

  onChangeStatus(value: boolean) {
    if (value) {
      this.executeService();
    }
  }

  ngOnDestroy() {
    if (this.dataChangeSubscription) {
      this.dataChangeSubscription.unsubscribe();
    }
    this.subscription?.unsubscribe();
  }

  // Table sorting - Logic
  onSortTable = (event: TableEvent) => {
    this.currentPage = 0;
    this.pagination.page = 0;
    if (event.event == 'clean') {
      this.sortOrder = '';
      this.sortFieldName = '';
    } else {
      this.sortOrder = event.event;
      this.sortFieldName = event.key ?? '';
    }
    this.executeService();
  };

  // Table action - Logic
  @Output() action: EventEmitter<TableEvent> = new EventEmitter<TableEvent>();

  onActionTable = (event: TableEvent) => this.action.emit(event);

  checkValue(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const labelElement = inputElement.nextElementSibling as HTMLElement;

    if (inputElement.value) {
      labelElement.style.display = 'none';
    } else {
      labelElement.style.display = 'block';
    }
  }

  // Table selects - Logic
  currentSelect: number = 0;
  toggleSelect = (
    selectPosition: number,
    select: [string, string | Function]
  ) => {
    this.currentSelect = selectPosition;
    this.typeStatus = select[1];
    this.statusCode = select[1];
    this.currentPage = 0;
    this.pagination.page = 0;
    // Search reset
    this.searchValue = '';
    this.executeService();
  };

  // Search - Logic
  searchValue: string = '';

  onSearch(searchValue: string) {
    this.searchValue = searchValue;
    this.executeService();
  }

  onPageChange(page: number): void {
    this.currentPage = page - 1;
    this.pagination.page = this.currentPage;
    this.executeService();
  }

  // Filter menu - Logic
  @Input() filter: boolean = false;
  showFilter: boolean = false;
  filterValues: FilterValues = {};
  @Input() moreFilterValues: FilterValues = {}
  @Input() responseData?: ResponseData

  toggleFilter = (state: boolean) => (this.showFilter = state);

  setFilters = (event?: any) => {
    this.filterValues.amountFrom = event.amountFrom;
    this.filterValues.amountTo = event.amountTo;
    this.filterValues.createDateFrom = event.dateFrom;
    // Creation date filters cannot be altered on this case
    if (!this.onlyLast30days) {
      this.filterValues.createDateFrom = event.dateFrom;
      this.filterValues.createDateTo = event.dateTo;
    }

    this.executeService();
  };

  onStartDateChange() {
    if (this.creationDate) {
      const startDate = new Date(this.creationDate.value);

      this.minEndDate = startDate.toISOString().split('T')[0];
    } else {
      this.dueDate?.setValue('');
    }
  }

  onEndDateChange() {
    if (this.dueDate) {
      const endDate = new Date(this.dueDate.value);

      this.maxDate = endDate.toISOString().split('T')[0];
    } else {
      this.creationDate?.setValue('');
    }
  }

  openCalendar(DateInput: HTMLInputElement): void {
    setTimeout(() => {
      DateInput.click();
    }, 100);
  }

  // Service calls
  executeService(searchValue?: string) {
    const pageable: Pageable = { ...this.pagination };
    if (this.sortFieldName && this.sortOrder) {
      pageable.sort = `${this.sortFieldName},${this.sortOrder}`;
    }
    let searchName = '';
    switch (this.service) {
      case 'supplier-accounts':
        combineLatest({
          currencies: this.ipCurrencyService.currencies$,
          supplierAccounts: this.fnAccountFinanceService.getSupplierAccounts(
            this.pagination,
            this.searchValue
          ),
        }).subscribe({
          next: ({ currencies, supplierAccounts }) => {
            if (currencies) {
              const newData: Array<FinSupplierAccountRow> = [];
              supplierAccounts.content.forEach((supplierAccount) => {
                const supplierAccountRow: FinSupplierAccountRow = {
                  ...supplierAccount,
                  formattedCardBalance: supplierAccount.cardBalance.toString(),
                  formattedAccountBalance:
                    supplierAccount.accountBalance.toString(),
                };
                const foundCurrency = currencies.find((currency) => {
                  // TODO: GET real currency
                  return currency.code === 'ARS';
                });
                if (foundCurrency) {
                  const formattedCardBalance = this.amountFormatPipe.transform(
                    supplierAccount.cardBalance,
                    foundCurrency.isCommaFloat,
                    foundCurrency.symbol
                    // foundCurrency.code
                  );
                  const formattedAccountBalance =
                    this.amountFormatPipe.transform(
                      supplierAccount.accountBalance,
                      foundCurrency.isCommaFloat,
                      foundCurrency.symbol
                      // foundCurrency.code
                    );
                  supplierAccountRow.formattedCardBalance =
                    formattedCardBalance;
                  supplierAccountRow.formattedAccountBalance =
                    formattedAccountBalance;
                }
                newData.push(supplierAccountRow);
              });
              this.data = newData;
              this.totalItems = supplierAccounts.totalElements;
            }
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'supplier-users':
        this.ipSupplierService.getSupplierUsers(pageable).subscribe({
          next: (data: any) => {
            this.data = data.content;
            this.totalItems = data.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'supplier':
        searchName = 'businessName';
        this.ipSupplierService
          .getSupplierManagers(pageable, searchName, this.searchValue)
          .subscribe({
            next: (data) => {
              const suppliers: SupplierResponse[] = data.content;
              const supplierRows: SupplierRow[] = [];
              suppliers.forEach((supplier) => {
                supplier.userInvopay.forEach((supplierUser) => {
                  const newSupplierUser: SupplierRow = {
                    ...supplierUser,
                    id: supplier.id,
                    businessName: supplier.businessName,
                    countryId: supplier.country?.id,
                    countryName: supplier.country?.name,
                    state: !supplier.deleted
                  };
                  supplierRows.push(newSupplierUser);
                });
              });
              this.data = supplierRows;
              this.totalItems = data.totalElements;
            },
            error: (e: IpErrorResponse) => {
              this.ipSnackbarService.showErrorMessage(e);
            },
          });
        break;
        case 'supplier-broker':
          searchName = 'businessName';
          this.ipSupplierService
            .getSupplierBrokerManagers(pageable, searchName, this.searchValue)
            .subscribe({
              next: (data) => {
                const suppliers: SupplierResponse[] = data.content;
                const supplierRows: SupplierRow[] = [];
                suppliers.forEach((supplier) => {
                  supplier.userInvopay.forEach((supplierUser) => {
                    const newSupplierUser: SupplierRow = {
                      ...supplierUser,
                      id: supplier.id,
                      businessName: supplier.businessName,
                      countryId: supplier.country?.id,
                      countryName: supplier.country?.name,
                      state: !supplier.deleted
                    };
                    supplierRows.push(newSupplierUser);
                  });
                });
                this.data = supplierRows;
                this.totalItems = data.totalElements;
              },
              error: (e: IpErrorResponse) => {
                this.ipSnackbarService.showErrorMessage(e);
              },
            });
          break;
      case 'invoice':
      case 'invoice-report':
      case 'invoice-paid':
        if (this.type === 'business') {
          searchName = 'fiscalIdOrSupplierName';
        } else {
          searchName = 'fiscalIdOrUsername';
        }
        // Sort replacements
        if (this.sortFieldName === 'supplierName') {
          this.sortFieldName = 'supplierBusinessName';
        } else if (this.sortFieldName === 'paymentDate') {
          this.sortFieldName = 'paymentProofPaymentDate';
        }

        combineLatest({
          currencies: this.ipCurrencyService.currencies$,
          invoices: this.tableBillService.getData(
            this.type,
            this.pagination,
            this.sortFieldName,
            this.sortOrder,
            searchName,
            this.searchValue,
            this.filterValues,
            Number(this.typeStatus),
            this.service === 'invoice-report',
            this.ignoredTypeStatus
          ),
        }).subscribe({
          next: ({ currencies, invoices }) => {
            if (currencies) {
              const newData: Array<InvoiceRow> = [];
              invoices.content.forEach((invoice) => {
                const newInvoice: InvoiceRow = {
                  ...invoice,
                  status: invoice.typeStatus.status,
                };
                const foundCurrency = currencies.find((currency) => {
                  return currency.id === invoice.currencyId;
                });
                if (foundCurrency) {
                  newInvoice.currencyCode = foundCurrency.code;

                  newInvoice.formattedAmount = this.amountFormatPipe.transform(
                    invoice.totalAmount,
                    foundCurrency.isCommaFloat,
                    foundCurrency.symbol,
                    foundCurrency.code
                  );
                }
                newData.push(newInvoice);
              });
              this.data = newData;
              this.totalItems = invoices.totalElements;
            }
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'invoice-supplier':
        this.subscription = this.route.paramMap
              .pipe(
                switchMap((params) => {
                  const selectedId = Number(params.get('id'));
                  return combineLatest({
                    currencies: this.ipCurrencyService.currencies$,
                    invoices: this.tableBillService.getData(
                      this.type,
                      this.pagination,
                      this.sortFieldName,
                      this.sortOrder,
                      searchName,
                      this.searchValue,
                      this.filterValues,
                      Number(this.typeStatus),
                      undefined,
                      undefined,
                      selectedId
                    ),
                  })
                })
              ).subscribe({
                next: ({ currencies, invoices }) => {
                  if (currencies) {
                    const newData: Array<InvoiceRow> = [];
                    invoices.content.forEach((invoice) => {
                      const newInvoice: InvoiceRow = {
                        ...invoice,
                        status: invoice.typeStatus.status,
                      };
                      const foundCurrency = currencies.find((currency) => {
                        return currency.id === invoice.currencyId;
                      });
                      if (foundCurrency) {
                        newInvoice.currencyCode = foundCurrency.code;

                        newInvoice.formattedAmount = this.amountFormatPipe.transform(
                          invoice.totalAmount,
                          foundCurrency.isCommaFloat,
                          foundCurrency.symbol,
                          foundCurrency.code
                        );
                      }
                      newData.push(newInvoice);
                    });
                    this.data = newData;
                    this.totalItems = invoices.totalElements;
                  }
                },
                error: (e: IpErrorResponse) => {
                  this.ipSnackbarService.showErrorMessage(e);
                },
              });
        break;
      case 'settlements':
          this.ipSettlementService.getData(
            this.pagination,
            this.sortFieldName,
            this.sortOrder,
            'description',
            this.searchValue,
            this.statusCode.toString(),
            this.billed,
            this.moreFilterValues
          ).subscribe({
          next: ( settlements ) => {
            this.ipCurrencyService.currencies$.subscribe({
              next: (currencies) => {
              if (!currencies) {
                return;
              }
              const newData: Array<SettlementRow> = [];
              settlements.content.forEach((settlement) => {
                const totalCommission =
                  settlement.settlementSummary.settlementCommissionAmount;
                const settlementCurrency =
                  settlement.settlementSummary.settlementCurrency;
                const foundCurrency = currencies.find((currency) => {
                  return currency.code === settlementCurrency;
                });
                const settlementCurrencyRels = settlement.settlementCurrencyRels;
                let formattedTotalAmount = 'N/A';
                if (settlementCurrencyRels && settlementCurrencyRels.length > 0) {
                  formattedTotalAmount = settlementCurrencyRels.map((rel) => {
                    const currencyRel = currencies.find((currency) => currency.code === rel.currencyCode);
                    if (currencyRel) {
                      return this.amountFormatPipe.transform(
                        rel.amount,
                        currencyRel.isCommaFloat,
                        currencyRel.symbol,
                        currencyRel.code
                      );
                    } else return rel.currencyCode + ' ' + this.amountFormatPipe.transform(rel.amount)
                  }).join('\n');
                } else if (foundCurrency) {
                  formattedTotalAmount = this.amountFormatPipe.transform(
                    totalCommission,
                    foundCurrency.isCommaFloat,
                    foundCurrency.symbol,
                    foundCurrency.code
                  );
                } else {
                  formattedTotalAmount = this.amountFormatPipe.transform(
                    totalCommission,
                    true,
                    '$',
                    settlementCurrency
                  );
                  console.warn(
                    "The settlement's currency wasn't found in the database."
                  );
                }
                newData.push({
                  ...settlement,
                  formattedTotalAmount,
                  statusText: settlement.status.statusText,
                });
              });
              this.data = newData;
              this.totalItems = settlements.totalElements;

              // MOBILE CONFIG
              this.mobileCardConfig = {
                headerKey: 'settlementId',
                // headerLabel: '#',
                headerKeyRight: 'status',
                subHeaderKeyRight: 'statusText',
                headerLabelRight: this.translateService.instant('IP.TABLE.SETTLEMENTS.STATUS_'),
                fields: [
                  { 
                    label: this.translateService.instant('IP.TABLE.SETTLEMENTS.CREATIONTIME'),
                    key: 'creationTime',
                    isDate: true
                  },
                  { label: this.translateService.instant('IP.TABLE.SETTLEMENTS.STARTDATESETTLEMENT'), key: 'startDateSettlement', isDate: true },
                  { label: this.translateService.instant('IP.TABLE.SETTLEMENTS.ENDDATESETTLEMENT'), key: 'endDateSettlement', isDate: true },
                  { label: this.translateService.instant('IP.TABLE.SETTLEMENTS.TOTALAMOUNT'), key: 'formattedTotalAmount', bold: true },
                ],
                showActionButton: true,
                actions: this.tableOption.actions
              };
            },
            });
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      case 'settlements-details':
        this.subscription = this.route.paramMap
              .pipe(
                switchMap((params) => {
                  const selectedId = Number(params.get('id'));
                  return combineLatest({
                    currencies: this.ipCurrencyService.currencies$,
                    settlements: this.ipSettlementService.getDataBySupplierId(
                      this.supplierId = selectedId,
                      this.pagination,
                      this.sortFieldName,
                      this.sortOrder,
                      'description',
                      this.searchValue,
                      this.statusCode.toString()
                    ),
                  })
                })
              )
              .subscribe({
              next: ({ currencies, settlements }) => {
            if (!currencies) {
              return;
            }
            const newData: Array<SettlementRow> = [];
            settlements.content.forEach((settlement) => {
              const totalCommission =
                settlement.settlementSummary.totalCommission;
              const settlementCurrency =
                settlement.settlementSummary.settlementCurrency;
              const foundCurrency = currencies.find((currency) => {
                return currency.code === settlementCurrency;
              });
              let formattedTotalAmount = 'N/A';
              if (foundCurrency) {
                formattedTotalAmount = this.amountFormatPipe.transform(
                  totalCommission,
                  foundCurrency.isCommaFloat,
                  foundCurrency.symbol,
                  foundCurrency.code
                );
              } else {
                formattedTotalAmount = this.amountFormatPipe.transform(
                  totalCommission,
                  true,
                  '$',
                  settlementCurrency
                );
                console.warn(
                  "The settlement's currency wasn't found in the database."
                );
              }
              newData.push({
                ...settlement,
                formattedTotalAmount,
                statusText: settlement.status.statusText,
              });
            });
            this.data = newData;
            this.totalItems = settlements.totalElements;
          },
          error: (e: IpErrorResponse) => {
            this.ipSnackbarService.showErrorMessage(e);
          },
        });
        break;
      default:
        console.warn('No service defined.');
        this.data = [];
        break;
    }
  }

  openFiltersModal() {
    const dialogRef = this.dialog.open(FnFiltersModalComponent, {
      height: 'max-content',
      width: '90%',
      maxWidth: 'none',
      panelClass: 'custom-modal',
      data: {
        optionalTitle: this.optionalTitle,
        startDate: this.datesForm.get('creationDate')?.value ?? '',
        endDate: this.datesForm.get('dueDate')?.value ?? '',
        searchValue: this.searchValue ?? '',
        executeService: this.executeService.bind(this),
      },
    });

    dialogRef.disableClose = true;

    dialogRef.componentInstance.filtersApplied.subscribe((filters: any) => {
      this.datesForm.patchValue({
        creationDate: filters.startDate,
        dueDate: filters.endDate,
      });
      if (filters.searchValue !== '') {
        this.searchValue = filters.searchValue;
      } else {
        this.searchValue = '';
      }
      this.executeService(this.searchValue);
    });
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {
    this.updateMode(event.target.innerWidth);
  }

  private updateMode(windowWidth: number) {
    if (windowWidth <= 1024) {
      this.mode = 'over';
    } else {
      this.mode = 'side';
    }
  }

  convertToDate(completeDateString: string): Date | string {
    if (completeDateString) {
      const dateParts = completeDateString.split('T')
      const parts = dateParts[0].split('-');
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      const partsHours = dateParts[1].split(':')
      const hours = parseInt(partsHours[0])
      const minutes = parseInt(partsHours[1])
      return new Date(year, month, day, hours, minutes);
    } else {
      return '';
    }
  }

  formatDate(date:Date) {
    const fechaISO = date.toISOString();
    return fechaISO.split('T')[0];
  }

  firstDay(date:Date) {
    const newDate = new Date(date);
    newDate.setDate(1);
    return newDate;
  }

  onSelectionChange(index: number): void {
    if (this.tableOption?.selects?.[index]) {
      this.toggleSelect(index, this.tableOption.selects[index]);
    }
  }
}
